import { HttpResponse } from "@/shared/config/globals";
import type { ReactNode } from "react";

//TODO: config~~に記載すべき
export const ErrorLevel = {
  Error: "error",
  Warn: "warn",
  Info: "info",
} as const;

export type ErrorLevelType = (typeof ErrorLevel)[keyof typeof ErrorLevel];

interface MoneyLinkErrorConstructorProps {
  level: ErrorLevelType;
  code: number;
  reason: string;
  messageTitle?: string;
  messageBody?: string;
}

export class MoneyLinkError {
  constructor(public props: MoneyLinkErrorConstructorProps) {}

  /**
   * 指定した MoneyLinkError オブジェクトを複製する
   * @param src 複製対象の MoneyLinkError オブジェクト
   * @returns 複製した MoneyLinkError オブジェクト
   */
  static copy(src: MoneyLinkError): MoneyLinkError {
    return new MoneyLinkError({
      level: src.props.level,
      code: src.props.code,
      reason: src.props.reason,
      messageTitle: src.props.messageTitle,
      messageBody: src.props.messageBody,
    });
  }
}

/** クライアントシステムのエラー */
export const ClientSystemError = {
  Destroyed: new MoneyLinkError({
    level: ErrorLevel.Error,
    code: -1,
    reason: "client_destroyed",
    messageTitle: undefined,
    messageBody:
      "予期せぬエラーが発生しました。時間をおいて再度やり直してください。\nやり直しても解決しない場合は、サポートサイトにお問い合わせください。",
  }),
  InvalidResponse: new MoneyLinkError({
    level: ErrorLevel.Error,
    code: -2,
    reason: "client_invalid_response",
    messageTitle: undefined,
    messageBody:
      "ただいまシステムが大変混み合っています。時間をおいて再度やり直してください。\nやり直しても解決しない場合は、サポートサイトにお問い合わせください。",
  }),
    ServerSideError: new MoneyLinkError({
    level: ErrorLevel.Error,
    code: -3,
    reason: "client_server_redirect_to_error_page",
    messageTitle: undefined,
    messageBody: "予期せぬエラーが発生しました。時間をおいて再度やり直してください。",
  }),
  ConnectionRequired: new MoneyLinkError({
    level: ErrorLevel.Warn,
    code: -1001,
    reason: "client_connection_required",
    messageTitle: undefined,
    messageBody: "ネットワーク接続に失敗しました。インターネット接続を確認して再実行してください。",
  }),
  UnLoadCamera: new MoneyLinkError({
    level: ErrorLevel.Warn,
    code: -1021,
    reason: "client_unload_camera",
    messageTitle: undefined,
    messageBody: "カメラが正常に起動していません。\n再度お試しください。",
  }),
} as const;

export type ClientSystemErrorType = (typeof ClientSystemError)[keyof typeof ClientSystemError];

class BaseError extends Error {
  constructor(e?: string) {
    super(e);
    this.name = new.target.name;
  }
}

/**
 * 警告ダイアログ表示用クラス
 * - 例外として throw するのではなく、setError() に直接渡して使用
 * - バリデーションエラーなどユーザーへの警告メッセージ表示に使用
 * @example
 * setError(new MoneyLinkWarning({
 *   title: "入力エラー",
 *   message: "パスワードは8文字以上で入力してください。",
 * }));
 */
export class MoneyLinkWarning extends BaseError {
  public title: string;
  public messageBody: ReactNode;
  public callback: Readonly<(...args: unknown[]) => unknown>;

  constructor({
    title = "確認",
    message,
    callback = () => { /**intentionally empty */ },
  }: {
    title?: string;
    message: ReactNode;
    callback?: Readonly<(...args: unknown[]) => unknown>;
  }) {
    super(typeof message === "string" ? message : undefined);
    this.title = title;
    this.messageBody = message;
    this.callback = callback;
  }
}

/** カスタム例外 */
export class MoneyLinkException extends BaseError {
  public error: MoneyLinkError;
  public callback: Readonly<(...args: unknown[]) => unknown>;

  /**
   * クライアントに起因する例外
   * @param props
   * @param {MoneyLinkError} props.error - エラー
   * @param {string?} props.e - 継承元Errorクラスの初期化パラメータ
   * @param {Readonly<(...args: unknown[]) => unknown>} props.callback - エラーに対する後続処理のコールバック
   */
  constructor({
    error,
    e,
    callback = () => { /**intentionally empty */ },
  }: {
    error: MoneyLinkError;
    e?: string;
    callback?: Readonly<(...args: unknown[]) => unknown>;
  }) {
    super(e);
    this.error = error;
    this.callback = callback;
  }

  /**
   * サーバに起因する例外
   * @param props
   * @param {HttpResponse} props.response - ApiClientのHTTPレスポンス
   * @returns {MoneyLinkException} kbException - KBException例外
   */
  static fromServer<T>({ response }: { response: HttpResponse<T> }): MoneyLinkException {
    // TODO: エラーコードやエラータイプによってレベル（ダイアログ表示かエラーページ表示か）を振り分ける
    // 例: 特定のエラーコードの場合はErrorLevel.Error（エラーページ）にするなど
    let level: ErrorLevelType = ErrorLevel.Warn; // とりあえず全てWarnにしてダイアログ表示対応
    if (response.errorType === "xxxxxxx") {
      level = ErrorLevel.Error;  // 例: エラータイプが "xxxxxxx" の場合はErrorLevel.Error（エラーページ）にする
    }

    return new MoneyLinkException({
      error: new MoneyLinkError({
        level: level,
        code: response.errorCode,
        reason: response.errorType,
        messageTitle: undefined,
        messageBody: response.errorMessage,
      }),
    });
  }

  /**
   * 発生したエラーに対する後続処理のコールバックをラップして返す
   * - ただし、MoneyLinkException エラーでない場合はラップせずにそのまま返す
   * @param {unknown} error 発生したエラー
   * @param {Readonly<(...args: unknown[]) => unknown>} callback エラーに対する後続処理
   * @returns コールバックがラップされた MoneyLinkException エラー
   */
  static wrapCallback(error: unknown, callback: Readonly<(...args: unknown[]) => unknown>): MoneyLinkException | unknown {
    // MoneyLinkException エラーでなければそのまま返す
    if (!(error instanceof MoneyLinkException)) {
      return error;
    }

    // エラーに対する後続処理のコールバックをラップして返す
    return new MoneyLinkException({
      error: MoneyLinkError.copy(error.error),
      callback: callback,
    });
  }
}
